import { useQuery } from '@tanstack/react-query';

import { api } from './api';

// ---- Widget catalog -------------------------------------------------------

// Stable IDs persisted in localStorage. Renaming a widget here without
// migrating the localStorage key drops user selections silently — the
// load() filter discards unknown IDs so the UI doesn't choke, but the
// user has to re-select. If you rename, also bump LOCAL_STORAGE_KEY.
export type WidgetId =
  // Inventory health
  | 'inventory_value'
  | 'low_stock'
  | 'zero_stock'
  | 'never_counted'
  | 'stale_count'
  | 'no_location'
  | 'value_by_category'
  | 'inventory_by_form'
  // Sourcing health
  | 'eol_primary'
  | 'no_supplier'
  | 'no_price'
  | 'stale_sync'
  | 'distributor_mix'
  | 'distributor_quotas'
  | 'lead_time_outliers'
  // Catalog shape
  | 'catalog_totals'
  | 'parts_by_category'
  | 'parts_by_mounting'
  | 'parts_by_lifecycle'
  | 'uncategorized'
  | 'rohs_coverage'
  | 'kicad_coverage'
  // Activity
  | 'recent_activity'
  | 'recent_eol'
  | 'recent_price_changes'
  // Admin
  | 'pending_account_requests'
  | 'active_invitations'
  // BOM
  | 'bom_totals'
  | 'cannot_build'
  | 'at_risk_boms'
  | 'popular_parts'
  | 'missing_inventory';

export type WidgetGroup =
  | 'Inventory'
  | 'Sourcing'
  | 'Catalog'
  | 'Activity'
  | 'Admin'
  | 'BOM';

export interface WidgetMeta {
  id: WidgetId;
  title: string;
  group: WidgetGroup;
  description: string;
  defaultEnabled?: boolean;
}

export const WIDGET_CATALOG: WidgetMeta[] = [
  // Inventory
  {
    id: 'inventory_value',
    title: 'Inventory totals',
    group: 'Inventory',
    description: 'Total parts, units on hand, and replacement value.',
    defaultEnabled: true,
  },
  {
    id: 'low_stock',
    title: 'Low stock',
    group: 'Inventory',
    description: 'Parts at or below their threshold.',
    defaultEnabled: true,
  },
  {
    id: 'zero_stock',
    title: 'Zero stock',
    group: 'Inventory',
    description: 'Parts with zero on hand and a non-zero threshold.',
  },
  {
    id: 'never_counted',
    title: 'Never counted',
    group: 'Inventory',
    description: 'Parts whose inventory has never been counted.',
  },
  {
    id: 'stale_count',
    title: 'Stale counts',
    group: 'Inventory',
    description: 'Parts not counted in 90+ days.',
  },
  {
    id: 'no_location',
    title: 'Stock without a location',
    group: 'Inventory',
    description: 'Parts that have stock but no assigned location.',
  },
  {
    id: 'value_by_category',
    title: '$ by category',
    group: 'Inventory',
    description: 'Where your inventory dollars are tied up.',
  },
  {
    id: 'inventory_by_form',
    title: 'Inventory by form',
    group: 'Inventory',
    description: 'Reels vs cut tape vs single, etc.',
  },
  // Sourcing
  {
    id: 'eol_primary',
    title: 'Parts with EOL primary',
    group: 'Sourcing',
    description: "Primary supplier's PN is dead — needs a replacement.",
    defaultEnabled: true,
  },
  {
    id: 'no_supplier',
    title: 'No supplier',
    group: 'Sourcing',
    description: 'Parts with no linked supplier at all.',
  },
  {
    id: 'no_price',
    title: 'No price',
    group: 'Sourcing',
    description: "Has a primary, but its price is missing.",
  },
  {
    id: 'stale_sync',
    title: 'Stale supplier sync',
    group: 'Sourcing',
    description: 'Primary supplier not synced in 30+ days.',
  },
  {
    id: 'distributor_mix',
    title: 'Distributor mix',
    group: 'Sourcing',
    description: 'How concentrated your primary suppliers are.',
  },
  {
    id: 'distributor_quotas',
    title: 'Distributor quotas (today)',
    group: 'Sourcing',
    description: "Today's API quota burn vs each distributor's daily cap.",
    defaultEnabled: true,
  },
  {
    id: 'lead_time_outliers',
    title: 'Lead time outliers',
    group: 'Sourcing',
    description: 'Primary supplier lead > 30 days.',
  },
  // Catalog
  {
    id: 'catalog_totals',
    title: 'Catalog totals',
    group: 'Catalog',
    description: 'Total parts, active vs obsolete.',
    defaultEnabled: true,
  },
  {
    id: 'parts_by_category',
    title: 'Parts by category',
    group: 'Catalog',
    description: 'Catalog shape: what dominates.',
    defaultEnabled: true,
  },
  {
    id: 'parts_by_mounting',
    title: 'Parts by mounting',
    group: 'Catalog',
    description: 'SMD / THT / Chassis split.',
  },
  {
    id: 'parts_by_lifecycle',
    title: 'Parts by lifecycle',
    group: 'Catalog',
    description: 'Active / NRND / obsolete / preview / unknown.',
  },
  {
    id: 'uncategorized',
    title: 'Uncategorized',
    group: 'Catalog',
    description: 'Parts with no category set.',
  },
  {
    id: 'rohs_coverage',
    title: 'RoHS coverage',
    group: 'Catalog',
    description: 'Compliant / non-compliant / unknown ratio.',
  },
  {
    id: 'kicad_coverage',
    title: 'KiCad coverage',
    group: 'Catalog',
    description: 'Tracked, with-3D-model, untracked counts.',
  },
  // Activity
  {
    id: 'recent_activity',
    title: 'Recent activity',
    group: 'Activity',
    description: 'Last 10 parts created or updated.',
    defaultEnabled: true,
  },
  {
    id: 'recent_eol',
    title: 'Recent EOL events',
    group: 'Activity',
    description: 'Suppliers flagged EOL in the last 30 days.',
  },
  {
    id: 'recent_price_changes',
    title: 'Recent price changes',
    group: 'Activity',
    description: 'Top movers from the last 30 days.',
  },
  // Admin
  {
    id: 'pending_account_requests',
    title: 'Pending account requests',
    group: 'Admin',
    description: "Requests waiting on someone's review.",
  },
  {
    id: 'active_invitations',
    title: 'Active invitations',
    group: 'Admin',
    description: 'Outstanding invites + soonest expiring.',
  },
  // BOM
  {
    id: 'bom_totals',
    title: 'BOM totals',
    group: 'BOM',
    description: 'Total BOMs and total lines.',
  },
  {
    id: 'cannot_build',
    title: 'Cannot build now',
    group: 'BOM',
    description: 'BOMs with at least one part below required quantity.',
    defaultEnabled: true,
  },
  {
    id: 'at_risk_boms',
    title: 'At-risk BOMs',
    group: 'BOM',
    description: 'BOMs with at least one EOL primary in their parts.',
  },
  {
    id: 'popular_parts',
    title: 'Most-used parts',
    group: 'BOM',
    description: 'Top 10 parts by BOM-line appearance count.',
  },
  {
    id: 'missing_inventory',
    title: 'Missing inventory',
    group: 'BOM',
    description: 'Parts in any BOM with zero stock.',
  },
];

export const WIDGET_GROUPS: WidgetGroup[] = [
  'Inventory',
  'Sourcing',
  'Catalog',
  'Activity',
  'Admin',
  'BOM',
];

const DEFAULT_WIDGETS: WidgetId[] = WIDGET_CATALOG.filter(
  (w) => w.defaultEnabled,
).map((w) => w.id);

const LOCAL_STORAGE_KEY = 'partforge.dashboard.v1.widgets';

const VALID_IDS = new Set<WidgetId>(WIDGET_CATALOG.map((w) => w.id));

export function loadEnabledWidgets(): WidgetId[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return [...DEFAULT_WIDGETS];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [...DEFAULT_WIDGETS];
    return parsed.filter(
      (id): id is WidgetId =>
        typeof id === 'string' && VALID_IDS.has(id as WidgetId),
    );
  } catch {
    return [...DEFAULT_WIDGETS];
  }
}

export function saveEnabledWidgets(ids: WidgetId[]): void {
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* localStorage unavailable — the next session falls back to defaults */
  }
}

// ---- Endpoint DTOs --------------------------------------------------------

export interface DashboardPartListItem {
  part_id: number;
  part_number: string;
  description: string | null;
  mfg_pn: string | null;
}

export interface CountByLabel {
  label: string;
  count: number;
}

export interface CategoryValueRow {
  label: string;
  parts_count: number;
  units_count: number;
  value_cents: number;
}

export interface InventoryDashboard {
  total_parts: number;
  total_units: number;
  total_value_cents: number;
  low_stock: DashboardPartListItem[];
  zero_stock: DashboardPartListItem[];
  never_counted: DashboardPartListItem[];
  stale_count: DashboardPartListItem[];
  no_location: DashboardPartListItem[];
  value_by_category: CategoryValueRow[];
  by_form: CountByLabel[];
}

export interface SupplierLeadRow {
  part_id: number;
  part_number: string;
  distributor_name: string;
  lead_time_days: number;
}

export interface SourcingDashboard {
  eol_primary: DashboardPartListItem[];
  no_supplier: DashboardPartListItem[];
  no_price: DashboardPartListItem[];
  stale_sync: DashboardPartListItem[];
  distributor_mix: CountByLabel[];
  lead_time_outliers: SupplierLeadRow[];
}

export interface CatalogDashboard {
  total_parts: number;
  active_parts: number;
  obsolete_parts: number;
  by_category: CountByLabel[];
  by_mounting: CountByLabel[];
  by_lifecycle: CountByLabel[];
  uncategorized_count: number;
  rohs_compliant: number;
  rohs_noncompliant: number;
  rohs_unknown: number;
  kicad_tracked: number;
  kicad_with_3d: number;
  kicad_total: number;
}

export interface ActivityRow {
  part_id: number;
  part_number: string;
  description: string | null;
  updated_at: string;
  created_at: string;
}

export interface EolEventRow {
  part_id: number;
  part_number: string;
  distributor_name: string;
  eol_reason: string | null;
  eol_detected_at: string | null;
}

export interface PriceMoveRow {
  part_id: number;
  part_number: string;
  distributor_name: string;
  old_cents: number;
  new_cents: number;
  pct_change: number;
  changed_at: string;
  /** Chronological cents-per-unit sequence for sparkline rendering. */
  series: number[];
}

export interface ActivityDashboard {
  recent_activity: ActivityRow[];
  recent_eol: EolEventRow[];
  recent_price_changes: PriceMoveRow[];
}

export interface AdminDashboard {
  pending_account_requests: number;
  active_invitations: number;
  soonest_invite_expires_at: string | null;
}

export interface BomReference {
  bom_id: number;
  bom_name: string;
}

export interface PopularPartRow {
  part_id: number;
  part_number: string;
  description: string | null;
  bom_appearances: number;
}

export interface BomDashboard {
  total_boms: number;
  total_lines: number;
  cannot_build: BomReference[];
  at_risk: BomReference[];
  popular_parts: PopularPartRow[];
  missing_inventory: DashboardPartListItem[];
}

export interface QuotaRow {
  distributor_slug: string;
  distributor_name: string;
  used_today: number;
  daily_limit: number;
  reset_at: string | null;
}

export interface QuotaDashboard {
  rows: QuotaRow[];
}

// ---- Hooks (one per grouped endpoint) -------------------------------------

const STALE_TIME_MS = 60_000;

export function useDashboardInventory(orgSlug: string | undefined) {
  return useQuery<InventoryDashboard>({
    queryKey: ['dashboard', 'inventory', orgSlug],
    queryFn: () =>
      api.get<InventoryDashboard>(
        `/api/orgs/${orgSlug}/dashboard/inventory`,
      ),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardSourcing(orgSlug: string | undefined) {
  return useQuery<SourcingDashboard>({
    queryKey: ['dashboard', 'sourcing', orgSlug],
    queryFn: () =>
      api.get<SourcingDashboard>(`/api/orgs/${orgSlug}/dashboard/sourcing`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardCatalog(orgSlug: string | undefined) {
  return useQuery<CatalogDashboard>({
    queryKey: ['dashboard', 'catalog', orgSlug],
    queryFn: () =>
      api.get<CatalogDashboard>(`/api/orgs/${orgSlug}/dashboard/catalog`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardActivity(orgSlug: string | undefined) {
  return useQuery<ActivityDashboard>({
    queryKey: ['dashboard', 'activity', orgSlug],
    queryFn: () =>
      api.get<ActivityDashboard>(`/api/orgs/${orgSlug}/dashboard/activity`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardAdmin(orgSlug: string | undefined) {
  return useQuery<AdminDashboard>({
    queryKey: ['dashboard', 'admin', orgSlug],
    queryFn: () =>
      api.get<AdminDashboard>(`/api/orgs/${orgSlug}/dashboard/admin`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardBom(orgSlug: string | undefined) {
  return useQuery<BomDashboard>({
    queryKey: ['dashboard', 'bom', orgSlug],
    queryFn: () =>
      api.get<BomDashboard>(`/api/orgs/${orgSlug}/dashboard/bom`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

export function useDashboardQuotas(orgSlug: string | undefined) {
  return useQuery<QuotaDashboard>({
    queryKey: ['dashboard', 'quotas', orgSlug],
    queryFn: () =>
      api.get<QuotaDashboard>(`/api/orgs/${orgSlug}/dashboard/quotas`),
    enabled: Boolean(orgSlug),
    staleTime: STALE_TIME_MS,
  });
}

// ---- Helpers --------------------------------------------------------------

export function widgetsByGroup(): Record<WidgetGroup, WidgetMeta[]> {
  const out = {} as Record<WidgetGroup, WidgetMeta[]>;
  for (const g of WIDGET_GROUPS) out[g] = [];
  for (const w of WIDGET_CATALOG) out[w.group].push(w);
  return out;
}
